import { InteractionRequiredAuthError } from '@azure/msal-browser'
import { msalInstance, loginRequest } from './msalConfig'
import { signalerSessionPerdue } from './sessionPerdue'
import { compteDeLaRequete, signalerAccesClientRefuse } from './accesClientRefuse'
import { CODE_RESEAU, TEXTE_RESEAU, texteParDefaut } from './apiErrorMessage'

const API_BASE = import.meta.env['VITE_API_BASE_URL'] as string ?? ''

/**
 * Issue #43 — une seule reprise de session à la fois.
 *
 * `getToken()` est traversé par **tous** les appels API. Sans ce drapeau, N appels concurrents qui
 * constatent ensemble la perte de session déclencheraient N redirections, et MSAL rejetterait les
 * suivantes en `interaction_in_progress`. Le module est jeté à la navigation : rien à réarmer.
 */
let sessionRecoveryStarted = false

/**
 * Plus **aucun** compte en cache : il n'y a pas de session, une interaction est requise par
 * définition. On renvoie donc l'utilisateur au login au lieu de le laisser sur un portail qui a
 * l'air connecté mais ne charge plus rien — c'est ce que le test T5 du 2026-08-29 a mis au jour :
 * le message technique « No active account » s'affichait dans la page, **sans aucune porte de sortie**.
 *
 * ⚠️ Ce geste ne peut pas reboucler, **et c'est vrai depuis ce lot seulement** : si le login échoue,
 * l'échec est capté avant le premier rendu (`authRedirectError`) et affiche l'écran d'erreur, dont
 * la garde arrête la relance. Avant, il aurait reproduit exactement la boucle de l'issue #43.
 *
 * Cas courant : la session Entra (cookie) survit au cache MSAL — la reprise est alors **transparente**,
 * un simple aller-retour SSO sans rien à saisir.
 */
async function recoverSession(): Promise<never> {
  if (!sessionRecoveryStarted) {
    sessionRecoveryStarted = true
    try {
      await msalInstance.loginRedirect(loginRequest)
    } catch {
      // Redirection impossible (interaction déjà en cours…) : on réarme pour ne pas condamner
      // l'utilisateur à l'erreur ci-dessous jusqu'au rechargement.
      sessionRecoveryStarted = false
      // Issue #57 — personne ne part chez Entra : il faut une porte de sortie à l'écran.
      signalerSessionPerdue()
    }
  }
  throw new ApiError(401, 'UNAUTHORIZED', 'Session expirée — reconnexion en cours…')
}

async function getToken(): Promise<string> {
  const accounts = msalInstance.getAllAccounts()
  if (accounts.length === 0) return recoverSession()

  try {
    const result = await msalInstance.acquireTokenSilent({
      ...loginRequest,
      account: accounts[0]!,
    })
    return result.accessToken
  } catch (e) {
    // Issue #43 — ce `catch` était NU : il expédiait l'utilisateur chez Microsoft sur *tout* échec
    // d'acquisition silencieuse, y compris une simple coupure réseau ou un `timed_out` d'iframe.
    // On ne redirige que lorsqu'une interaction est réellement requise ; le reste remonte comme
    // une erreur d'API, visible et diagnosticable. (Même classe de défaut que l'issue MSAL #8434,
    // et surface qu'emprunteront les refus de #46 phase 2.)
    if (e instanceof InteractionRequiredAuthError) {
      if (!sessionRecoveryStarted) {
        sessionRecoveryStarted = true
        await msalInstance.acquireTokenRedirect(loginRequest)
      }
      throw new ApiError(401, 'UNAUTHORIZED', 'Session expirée — reconnexion en cours…')
    }
    // Issue #57 — c'est CE chemin qui affichait « Impossible d'obtenir un jeton d'accès » dans la
    // page, sans aucun bouton : l'échec n'exige pas d'interaction (iframe expirée, coupure pendant
    // le renouvellement), donc `apiClient` ne redirige délibérément pas — leçon de l'issue #43.
    // Mais ne pas rediriger ne dispense pas d'offrir la sortie : on lève le drapeau, la racine
    // affiche la bannière, et l'utilisateur décide.
    signalerSessionPerdue()
    throw new ApiError(401, 'UNAUTHORIZED', "Impossible d'obtenir un jeton d'accès")
  }
}

export class ApiError extends Error {
  public readonly fields?: Record<string, string>
  /**
   * Données contextuelles supplémentaires renvoyées dans le body d'erreur (autres clés que
   * `error`, `message`, `fields`). Utilisé p. ex. par spec 051 pour `existingScenarioId` sur
   * 409 DUPLICATE_NAME.
   */
  public readonly extra?: Record<string, unknown>

  constructor(
    public readonly status: number,
    public readonly code: string,
    message?: string,
    fields?: Record<string, string>,
    extra?: Record<string, unknown>
  ) {
    super(message ?? code)
    this.fields = fields
    this.extra = extra
  }
}

/**
 * Issue #57 — la seule porte par laquelle `fetch` est appelé, et la seule qui traduit son échec.
 *
 * `fetch` ne rejette que lorsque la requête **n'atteint jamais** le serveur — coupure, DNS, CORS,
 * onglet hors ligne. Le libellé du rejet appartient alors au moteur : Firefox écrit « NetworkError
 * when attempting to fetch resource », Chrome « Failed to fetch », Safari « Load failed ». Ces
 * phrases remontaient telles quelles jusque dans les bannières des pages.
 *
 * La correction est posée **ici** plutôt que dans les 161 endroits qui affichent `err.message` :
 * une traduction au point d'émission couvre tout le portail, y compris les écrans qui n'existent
 * pas encore. Le libellé d'origine n'est pas perdu — il part dans `extra.detailTechnique`, que
 * `messageUtilisateur` sait ressortir pour le diagnostic.
 */
async function fetchTraduit(url: string, init?: RequestInit): Promise<Response> {
  try {
    return await fetch(url, init)
  } catch (e) {
    const brut = e instanceof Error ? e.message : String(e)
    throw new ApiError(0, CODE_RESEAU, TEXTE_RESEAU, undefined, { detailTechnique: brut })
  }
}

async function doFetch(path: string, token: string, init?: RequestInit): Promise<Response> {
  // Pour les uploads multipart (body = FormData), ne pas forcer Content-Type :
  // le browser ajoute automatiquement `multipart/form-data; boundary=...`.
  // Forcer `application/json` casse le parsing côté Functions (`request.formData()`).
  const isFormData = typeof FormData !== 'undefined' && init?.body instanceof FormData

  const headers: Record<string, string> = {
    Authorization: `Bearer ${token}`,
    ...(init?.headers as Record<string, string> | undefined),
  }
  if (!isFormData && !headers['Content-Type']) {
    headers['Content-Type'] = 'application/json'
  }

  return fetchTraduit(`${API_BASE}${path}`, {
    ...init,
    headers,
  })
}

// US3 (073) — Coalescing des requêtes GET « en vol ».
// Au boot, plusieurs composants demandent le même GET en parallèle (mesuré spec 065 :
// préférences ×3, alertes ×2, comptes ×2). On fond les GET identiques concurrents en une
// seule requête : les co-appelants partagent la même promesse. Coalescing **en vol
// uniquement** — l'entrée est retirée à la résolution/au rejet, donc aucun cache et aucune
// donnée périmée après une mutation. **GET sans body uniquement** (jamais POST/PATCH/DELETE).
const inflightGets = new Map<string, Promise<unknown>>()

function isCoalesceableGet(init?: RequestInit): boolean {
  const method = (init?.method ?? 'GET').toUpperCase()
  return method === 'GET' && init?.body == null
}

function activeAccountKey(): string {
  // Inclure l'identité du compte actif dans la clé : ne jamais mutualiser deux contextes d'auth.
  return msalInstance.getAllAccounts()[0]?.homeAccountId ?? 'anon'
}

export async function apiFetch<T>(path: string, init?: RequestInit): Promise<T> {
  if (!isCoalesceableGet(init)) {
    return apiFetchInner<T>(path, init)
  }
  const key = `GET ${activeAccountKey()} ${path}`
  const existing = inflightGets.get(key) as Promise<T> | undefined
  if (existing) return existing
  const promise = apiFetchInner<T>(path, init)
  inflightGets.set(key, promise)
  try {
    return await promise
  } finally {
    inflightGets.delete(key)
  }
}

async function apiFetchInner<T>(path: string, init?: RequestInit): Promise<T> {
  const token = await getToken()
  let response = await doFetch(path, token, init)

  if (response.status === 401) {
    const accounts = msalInstance.getAllAccounts()
    try {
      const refreshed = await msalInstance.acquireTokenSilent({
        ...loginRequest,
        account: accounts[0]!,
        forceRefresh: true,
      })
      response = await doFetch(path, refreshed.accessToken, init)
    } catch (e) {
      // Même garde que `recoverSession` : sans elle, N appels concurrents en 401 déclencheraient
      // N redirections, et MSAL rejetterait les suivantes en `interaction_in_progress` — une erreur
      // de plus par-dessus la première. (Audit de clôture #43, 2026-08-29.)
      if (e instanceof InteractionRequiredAuthError && !sessionRecoveryStarted) {
        sessionRecoveryStarted = true
        await msalInstance.loginRedirect(loginRequest)
      } else {
        // Issue #57 — aucune redirection n'a été lancée ici (échec sans interaction requise, ou
        // reprise déjà en cours ailleurs). Sans signal, l'appel remonte un 401 que chaque page
        // affiche dans son propre coin : N bannières techniques pour un seul événement.
        signalerSessionPerdue()
      }
      throw new ApiError(401, 'UNAUTHORIZED', 'Session expirée — reconnexion en cours…')
    }
  }

  if (!response.ok) {
    const body = await response.json().catch(() => ({})) as { error?: string; message?: string; fields?: Record<string, string> } & Record<string, unknown>
    const { error: _e, message: _m, fields: _f, ...extra } = body
    // Issue #57 — quand l'API ne joint aucun message, `ApiError` posait `message = code` : les
    // pages affichaient « UNKNOWN » ou « INTERNAL_ERROR ». Le repli est désormais la phrase de la
    // famille ; le code technique reste lisible dans `.code`, jamais perdu.
    const code = body.error ?? 'UNKNOWN'
    // Spec 160 (D-160-06) — le refus d'accès AU CLIENT, et lui seul parmi les 403 du serveur, lève le signal que la
    // coquille traduit en écran « Accès refusé ». L'erreur remonte quand même à l'écran qui l'a provoquée.
    if (response.status === 403 && code === 'ACCOUNT_ACCESS_DENIED') signalerAccesClientRefuse(compteDeLaRequete(path))
    throw new ApiError(
      response.status,
      code,
      body.message ?? texteParDefaut(response.status),
      body.fields,
      extra
    )
  }

  if (response.status === 204) return undefined as T
  return response.json() as Promise<T>
}

export async function apiBlobFetch(
  path: string,
  fallbackName = 'export.xlsx',
): Promise<{ blob: Blob; filename: string }> {
  const token = await getToken()
  const response = await fetchTraduit(`${API_BASE}${path}`, {
    headers: { Authorization: `Bearer ${token}` },
  })

  if (!response.ok) {
    const body = await response.json().catch(() => ({})) as { error?: string; message?: string }
    throw new ApiError(response.status, body.error ?? 'UNKNOWN', body.message ?? texteParDefaut(response.status))
  }

  // Nom d'origine (nommage runbook) si le header est lisible ; sinon repli explicite et parlant
  // fourni par l'appelant (le header peut être masqué cross-origin si non exposé côté API).
  const contentDisposition = response.headers.get('Content-Disposition') ?? ''
  const match = contentDisposition.match(/filename="?([^";\r\n]+)"?/)
  const filename = match?.[1] ?? fallbackName
  const blob = await response.blob()
  return { blob, filename }
}

/** Public endpoint — no auth required */
export async function apiPublicFetch<T>(path: string): Promise<T> {
  const response = await fetchTraduit(`${API_BASE}${path}`)
  if (!response.ok) throw new ApiError(response.status, 'FETCH_ERROR', texteParDefaut(response.status))
  return response.json() as Promise<T>
}
